import { ADVISORS_BASE } from '@src/components/sco/Advisors/constants'
import { resolveAdvisorsBase } from '@src/components/sco/Advisors/paths'
import { BaseballCardData } from '@src/components/sco/Advisors/types'
import { normalizeAsPath } from '@src/components/sco/Advisors/utils'
import { ADVISOR_ENGAGEMENT_STATUS_ACTIVE } from '@src/constants/advisor-engagement-statuses'
import { ScoAdvisorEngagement } from '@src/graphql-types'

export const myFoundersHref = (founderUserId?: string | null, base: string = ADVISORS_BASE) => {
  const root = `${base}/my-founders`
  return founderUserId ? `${root}?founderUserId=${founderUserId}` : root
}

export const inboxFounderHref = (founderUserId?: string | null, base: string = ADVISORS_BASE) => {
  const root = `${base}/inbox`
  return founderUserId ? `${root}?founderUserId=${encodeURIComponent(founderUserId)}` : root
}

/** Stable founder id from either denormalized column or nested user. */
export const founderUserIdOf = (engagement?: ScoAdvisorEngagement | null) =>
  engagement?.founderUserId || engagement?.founderUser?.userId || null

export const founderMemberProfileHref = (engagement: ScoAdvisorEngagement) => {
  const user = engagement.founderUser
  const userId = founderUserIdOf(engagement)
  if (!userId) {
    return undefined
  }
  return `/members/${user?.slug || userId}`
}

export const isFounderChatActive = (
  engagement: ScoAdvisorEngagement,
  asPath: string,
  selectedFounderUserId?: string
) => {
  const path = normalizeAsPath(asPath)
  const base = resolveAdvisorsBase(asPath)
  const founderUserId = founderUserIdOf(engagement)
  if (!founderUserId || !selectedFounderUserId) {
    return false
  }

  // Chat is /my-founders?founderUserId=… — landing (no param) and View All stay inactive.
  if (path === `${base}/my-founders/all`) {
    return false
  }

  return path.startsWith(`${base}/my-founders`) && selectedFounderUserId === founderUserId
}

export const founderDisplayName = (engagement: ScoAdvisorEngagement) =>
  engagement.founderUser?.fullName || engagement.founderUser?.displayName || 'Founder'

export const founderSubtitle = (engagement: ScoAdvisorEngagement) =>
  engagement.company?.name || engagement.category?.title || engagement.whyText?.slice(0, 60) || 'Founder'

const founderBio = (engagement: ScoAdvisorEngagement) => {
  const user = engagement.founderUser
  const fromUser = user?.shortBio?.trim() || user?.title?.trim() || ''
  if (fromUser) {
    return fromUser
  }
  const companyPitch = engagement.company?.elevatorPitch?.trim() || user?.company?.elevatorPitch?.trim()
  if (companyPitch) {
    return companyPitch
  }
  return engagement.whyText?.trim() || 'Founder on your advisory roster.'
}

const founderRole = (engagement: ScoAdvisorEngagement): { title: string; palette: string } => {
  if (engagement.category?.title) {
    return { title: engagement.category.title, palette: 'orange' }
  }
  const companyName = engagement.company?.name || engagement.founderUser?.company?.name
  if (companyName) {
    return { title: companyName, palette: 'orange' }
  }
  return { title: 'Founder', palette: 'orange' }
}

/** Card data for the My Founders landing grid (mirrors advisor BaseballCards). */
export const engagementToFounderBaseballCard = (
  engagement: ScoAdvisorEngagement,
  options?: { base?: string }
): BaseballCardData => {
  const base = options?.base ?? ADVISORS_BASE
  const founderUserId = founderUserIdOf(engagement)
  const name = founderDisplayName(engagement)
  const { title: role, palette: rolePalette } = founderRole(engagement)
  const profileHref = founderMemberProfileHref(engagement)

  return {
    name,
    role,
    rolePalette,
    bio: founderBio(engagement),
    avatarUrl: engagement.founderUser?.pictureUrl || undefined,
    responses: '—',
    founders: '—',
    primaryLabel: 'Message',
    primaryHref: inboxFounderHref(founderUserId, base),
    primaryPaywall: 'paid',
    secondaryLabel: 'View profile',
    secondaryHref: profileHref,
    secondaryExternal: false,
  }
}

export const isActiveFounderEngagement = (engagement?: ScoAdvisorEngagement | null) =>
  !!founderUserIdOf(engagement) && engagement.statusKey === ADVISOR_ENGAGEMENT_STATUS_ACTIVE

export const findFounderEngagement = (engagements: ScoAdvisorEngagement[], founderUserId?: string | null) => {
  if (!founderUserId) {
    return undefined
  }
  return engagements.find(engagement => founderUserIdOf(engagement) === founderUserId)
}
